import {
  trackEvent,
  trackFunnelEvent,
  getOrCreateSessionId,
} from "./ga";
import { getActiveExperiments } from "./experiments";
import { faceAiDemoSignalProps } from "./face-ai-demo-signals";

type EventCategory =
  | "pageview"
  | "acquisition"
  | "tool_engagement"
  | "monetization"
  | "retention"
  | "system";

interface TrackOptions {
  skipGA?: boolean;
}

// === PFP-ROOT COOKIE HELPER ===
// Sets a cookie visible across all pfpmaker.com subdomains.

function setPfpCookie(name: string, value: string, maxAgeDays: number = 365) {
  if (typeof window === "undefined") return;
  const maxAge = maxAgeDays * 86400;
  document.cookie = `${name}=${encodeURIComponent(value)}; Domain=.pfpmaker.com; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
}

/** Strips a query param from the current URL without a page reload. */
function stripUrlParam(url: string, param: string): string {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete(param);
    return u.href;
  } catch {
    return url;
  }
}

// === VISITOR ID ===
// Priority chain:
//   1. URL param ?pfp_vid=           — landing-page clickthrough (most authoritative)
//   2. Domain cookie pfp_visitor_id   — cross-subdomain persistence
//   3. localStorage pfp_visitor_id    — same-subdomain persistence
//   4. Generate fresh value

export function getOrCreateVisitorId(): string {
  if (typeof window === "undefined") return "";

  const url = window.location.href;

  // 1. URL param — set by landing-page CTA decoration
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get("pfp_vid");
    if (fromUrl) {
      localStorage.setItem("pfp_visitor_id", fromUrl);
      setPfpCookie("pfp_visitor_id", fromUrl);
      window.history.replaceState({}, "", stripUrlParam(url, "pfp_vid"));
      return fromUrl;
    }
  } catch { /* non-critical */ }

  // 2. Domain cookie — set by landing server or by JS on first visit
  try {
    const match = document.cookie.match(/(?:^|;\s*)pfp_visitor_id=([^;]+)/);
    if (match) {
      const fromCookie = decodeURIComponent(match[1]);
      localStorage.setItem("pfp_visitor_id", fromCookie);
      return fromCookie;
    }
  } catch { /* non-critical */ }

  // 3-4. localStorage fallback or fresh
  let visitorId = localStorage.getItem("pfp_visitor_id");
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
    localStorage.setItem("pfp_visitor_id", visitorId);
  }
  return visitorId;
}

// === GA CLIENT ID ===

function getGAClientId(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(.+?)(?:;|$)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

// === RAW SNAPSHOT ===

function collectRawClientData(): Record<string, unknown> {
  const cached = sessionStorage.getItem("pfp_raw_snapshot");
  if (cached) {
    try {
      const snapshot = JSON.parse(cached);
      snapshot.url = window.location.href;
      snapshot.title = document.title || null;
      return snapshot;
    } catch {
      /* fall through */
    }
  }

  const params = new URLSearchParams(window.location.search);
  const snapshot: Record<string, unknown> = {
    url: window.location.href,
    referrer: document.referrer || null,
    title: document.title || null,
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    utm_term: params.get("utm_term") || null,
    utm_content: params.get("utm_content") || null,
    userAgent: navigator.userAgent || null,
    language: navigator.language || null,
    languages: Array.from(navigator.languages || []),
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    screenWidth: screen.width || null,
    screenHeight: screen.height || null,
    viewportWidth: window.innerWidth || null,
    viewportHeight: window.innerHeight || null,
    devicePixelRatio: window.devicePixelRatio || null,
    maxTouchPoints: navigator.maxTouchPoints ?? null,
    hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    connectionType:
      (navigator as unknown as Record<string, any>).connection?.effectiveType ||
      null,
  };

  sessionStorage.setItem("pfp_raw_snapshot", JSON.stringify(snapshot));
  return snapshot;
}

// === ATTRIBUTION ===

function parseReferrerDomain(referrer: string): string | null {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname;
  } catch {
    return null;
  }
}

function parseLandingPage(url: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

function inferMedium(
  referrer: string | null,
  utmMedium: string | null
): string {
  if (utmMedium) return utmMedium;
  if (!referrer) return "direct";
  const domain = parseReferrerDomain(referrer);
  if (!domain) return "direct";
  if (/google\.|bing\.|yahoo\.|duckduckgo\.|baidu\.|ecosia\./.test(domain))
    return "organic";
  if (
    /facebook\.|instagram\.|twitter\.|x\.com|linkedin\.|tiktok\.|youtube\.|reddit\.|pinterest\./.test(
      domain
    )
  )
    return "social";
  return "referral";
}

function inferSource(
  referrer: string | null,
  utmSource: string | null
): string | null {
  if (utmSource) return utmSource;
  return parseReferrerDomain(referrer || "") || null;
}

function buildTouchData(rawUrl: string, rawReferrer: string | null) {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(new URL(rawUrl).search);
  } catch {
    params = new URLSearchParams();
  }
  const rawUtmSource = params.get("utm_source") || null;
  const rawUtmMedium = params.get("utm_medium") || null;

  return {
    raw_referrer: rawReferrer,
    raw_url: rawUrl,
    raw_utm_source: rawUtmSource,
    raw_utm_medium: rawUtmMedium,
    raw_utm_campaign: params.get("utm_campaign") || null,
    raw_utm_term: params.get("utm_term") || null,
    raw_utm_content: params.get("utm_content") || null,
    referrerDomain: parseReferrerDomain(rawReferrer || ""),
    landingPage: parseLandingPage(rawUrl),
    medium: inferMedium(rawReferrer, rawUtmMedium),
    source: inferSource(rawReferrer, rawUtmSource),
    timestamp: new Date().toISOString(),
  };
}

export function captureAttribution() {
  if (typeof window === "undefined") return;

  const referrer = document.referrer || null;
  const ownDomain = window.location.hostname;
  const isInternalReferrer =
    referrer && parseReferrerDomain(referrer) === ownDomain;

  const touchData = buildTouchData(
    window.location.href,
    isInternalReferrer ? null : referrer
  );

  if (!localStorage.getItem("pfp_first_touch")) {
    localStorage.setItem("pfp_first_touch", JSON.stringify(touchData));
  }

  if (!isInternalReferrer) {
    sessionStorage.setItem("pfp_last_touch", JSON.stringify(touchData));
  }
}

function getStoredAttribution(): {
  firstTouch: Record<string, unknown>;
  lastTouch: Record<string, unknown>;
} {
  if (typeof window === "undefined")
    return { firstTouch: {}, lastTouch: {} };
  let firstTouch = {};
  let lastTouch = {};
  try {
    firstTouch = JSON.parse(
      localStorage.getItem("pfp_first_touch") || "{}"
    );
  } catch {
    /* empty */
  }
  try {
    lastTouch = JSON.parse(
      sessionStorage.getItem("pfp_last_touch") || "{}"
    );
  } catch {
    /* empty */
  }
  return { firstTouch, lastTouch };
}

// === CLIENT-SIDE DEDUP (guards against React Strict Mode double-effects) ===

const recentlySent = new Set<string>();

function isDuplicate(eventName: string, propsFingerprint: string): boolean {
  const key = `${eventName}:${propsFingerprint}`;
  if (recentlySent.has(key)) return true;
  recentlySent.add(key);
  setTimeout(() => recentlySent.delete(key), 500);
  return false;
}

// === SEND TO OWN BACKEND ===

function sendToOwnAnalytics(payload: Record<string, unknown>) {
  try {
    const fp = `${payload.eventName}:${JSON.stringify(payload.properties)}`;
    if (isDuplicate(payload.eventName as string, fp)) return;

    const blob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/f", blob);
    } else {
      fetch("/api/f", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        keepalive: true,
      });
    }
  } catch {
    // Silently fail — analytics should never break the app
  }
}

// === CORE PAYLOAD BUILDER ===

function resolveIsNewVisitor(): boolean {
  // Only the very first event for a never-before-seen visitor returns true.
  // Once we've sent isNewVisitor=true once, all subsequent events return false.
  const SENT_KEY = "pfp_new_visitor_sent";
  if (sessionStorage.getItem(SENT_KEY)) return false;

  const isNew = !localStorage.getItem("pfp_visitor_seen");
  if (isNew) {
    localStorage.setItem("pfp_visitor_seen", "true");
    sessionStorage.setItem(SENT_KEY, "1");
  }
  return isNew;
}

function buildPayload(
  eventName: string,
  category: EventCategory,
  properties: Record<string, unknown>
) {
  const raw = collectRawClientData();
  const { firstTouch, lastTouch } = getStoredAttribution();
  const visitorId = getOrCreateVisitorId();

  // Session traits (Face AI AgeGenderNet) — under properties so every event
  // (funnel, A/B, pay, …) can segment by gn/ag once known. Event props win.
  const sessionTraits = faceAiDemoSignalProps();

  return {
    eventName,
    eventCategory: category,
    properties: {
      ...sessionTraits,
      ...properties,
    },
    sessionId: getOrCreateSessionId(),
    visitorId,
    gaClientId: getGAClientId(),
    experiments: getActiveExperiments(visitorId),
    raw,
    firstTouch,
    lastTouch,
    gaEventName: eventName,
    isNewVisitor: resolveIsNewVisitor(),
    isAuthenticated: !!sessionStorage.getItem("pfp_user_id"),
    clientEventId: crypto.randomUUID(),
  };
}

// === PRE-AUTH COOKIE ===
// Stores visitorId + sessionId in a short-lived cookie so the server-side
// NextAuth callback can stitch server events to the real browsing session.

export function storeAnalyticsIdentityForAuth() {
  if (typeof window === "undefined") return;
  const visitorId = getOrCreateVisitorId();
  const sessionId = getOrCreateSessionId();
  const value = JSON.stringify({ visitorId, sessionId });
  document.cookie = `pfp_analytics_id=${encodeURIComponent(value)}; path=/; max-age=300; SameSite=Lax`;
}

// === LAST-TOOL TRACKING (secondary purchase attribution) ===
// Remembers the most recent tool the visitor loaded (tool_engagement_tool_loaded
// carries tool_id). Monetization events stamp this as `last_tool_id` so purchases
// can be attributed to a hero tool even when the paywall context (tool_name) is
// absent — e.g. voluntary purchases straight from the pricing page.

const LAST_TOOL_KEY = "pfp_last_tool_id";

export function setLastToolId(toolId: string): void {
  if (typeof window === "undefined" || !toolId) return;
  try {
    sessionStorage.setItem(LAST_TOOL_KEY, toolId);
  } catch {}
}

export function getLastToolId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(LAST_TOOL_KEY);
  } catch {
    return null;
  }
}

// === PUBLIC API ===

export function track(
  eventName: string,
  category: EventCategory,
  properties: Record<string, unknown> = {},
  options: TrackOptions = {}
) {
  if (typeof window === "undefined") return;

  if (!options.skipGA) {
    trackEvent(eventName, {
      ...(properties as Record<string, any>),
      funnel_type: category,
    });
  }

  sendToOwnAnalytics(buildPayload(eventName, category, properties));
}

export function trackFunnel(
  funnelType: "acquisition" | "tool_engagement" | "monetization" | "retention",
  step: string,
  properties: Record<string, unknown> = {}
) {
  if (typeof window === "undefined") return;

  trackFunnelEvent(funnelType, step, properties as Record<string, any>);

  const category: EventCategory =
    funnelType === "acquisition"
      ? "acquisition"
      : funnelType === "tool_engagement"
        ? "tool_engagement"
        : funnelType === "monetization"
          ? "monetization"
          : "retention";

  // Record the last tool engaged (for secondary purchase attribution).
  if (funnelType === "tool_engagement" && step === "tool_loaded" && properties.tool_id) {
    setLastToolId(String(properties.tool_id));
  }

  sendToOwnAnalytics(
    buildPayload(`${funnelType}_${step}`, category, {
      ...properties,
      funnel_type: funnelType,
      funnel_step: step,
    })
  );
}
